// Pure permission rules shared by usePermission and RoleProtectedRoute (and unit-tested on their own): the same
// answers the server gives, computed from GET /v1/web/user-permissions/effective.
import { OWNER_ONLY_COMPONENTS } from '@/config/pagePaths.generated.js';

export const LEVEL_RANK = { NO_ACCESS: 0, READ_ONLY: 1, FULL_ACCESS: 2 };

/** true when `level` is at least `required`. Unknown levels rank as NO_ACCESS. */
export const meets = (level, required = 'READ_ONLY') => (LEVEL_RANK[level] || 0) >= (LEVEL_RANK[required] || 0);

/**
 * The level for a page id ("/game/segments") or a component id ("/game/segments.mutate-segment"), or null while
 * permissions are still loading.
 *
 * - A page resolves to its own grant, else the role default, else NO_ACCESS.
 * - A component resolves to its own grant (the server keys it "<page>_<component>"), else its page's level, and is
 *   capped at its page: a NO_ACCESS page means NO_ACCESS for every component under it.
 * - An owner-only component (rbac-config.json ownerOnly) follows ownership, not its page's level: it is not capped
 *   at a READ_ONLY page (a game owner reads Admin > Games and still edits their own game), only closed by a
 *   NO_ACCESS one.
 */
export const resolvePermissionLevel = (effectivePermissions, id) => {
    if (!effectivePermissions) return null;
    const fallback = effectivePermissions.defaultPermissionLevel || 'NO_ACCESS';
    if (!id) return fallback;

    const lastDot = id.lastIndexOf('.');
    if (lastDot <= 0) {
        return effectivePermissions.pagePermissions?.[id] || fallback;
    }
    const pageId = id.slice(0, lastDot);
    const apiKey = `${pageId}_${id.slice(lastDot + 1)}`;
    const pageLevel = effectivePermissions.pagePermissions?.[pageId] || fallback;
    if (pageLevel === 'NO_ACCESS') return 'NO_ACCESS';
    const componentLevel = effectivePermissions.componentPermissions?.[apiKey] || pageLevel;
    if (OWNER_ONLY_COMPONENTS.includes(id)) return componentLevel;
    return (LEVEL_RANK[componentLevel] || 0) > (LEVEL_RANK[pageLevel] || 0) ? pageLevel : componentLevel;
};

/**
 * What a guarded route does: 'wait' (permissions or the super-user flag not loaded yet), 'allow', 'soft-deny'
 * (land on an accessible page instead) or 'hard-deny' (/not-authorized - the super-user gate is not grantable).
 */
export const routeAccessDecision = ({
    superUserOnly = false, isSuperUser = false, userDetailsLoaded = true,
    pagePath, pageLevel, anyOfPagePath, altPageLevel,
    componentPath, componentLevel, minLevel = 'READ_ONLY',
}) => {
    if (superUserOnly && !userDetailsLoaded) return 'wait';
    if (superUserOnly && !isSuperUser) return 'hard-deny';
    if (pagePath && pageLevel === null) return 'wait';
    if (anyOfPagePath && altPageLevel === null) return 'wait';
    if (pagePath) {
        const primaryOk = pageLevel !== 'NO_ACCESS';
        const altOk = anyOfPagePath ? altPageLevel !== 'NO_ACCESS' : false;
        if (!primaryOk && !altOk) return 'soft-deny';
    }
    if (componentPath) {
        if (componentLevel === null) return 'wait';
        if (!meets(componentLevel, minLevel)) return 'soft-deny';
    }
    return 'allow';
};
